import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import * as Sentry from "@sentry/browser"
import { humanizeString } from "@utils/stringMinipulation"
import { api, BulkTransferPayload, ClientListFilters } from "../api"
import { Button } from "@components/Button"
import { BulkTransferModal } from "../components/BulkTransferModal"
import { ConfirmDialog } from "@UILibrary/Modal"
import { SearchBox } from "@UILibrary/SearchBox"
import { SearchableSelect } from "@UILibrary/SearchableSelect"
import { Table, TableColumn, SortState } from "@UILibrary/Table"

type ClientRow = {
  id: string
  attributes: {
    fullName: string
    firstNames: string
    lastName: string
    email: string
    cellphoneNumber: string
    dateOfBirth: string | null
    status: string
    active: boolean
    completedOnboardingAt: string | null
    wealthManagerId: number | null
    wealthManagerName: string | null
  }
}

type Props = { organizationId: number }

const STATUS_OPTIONS = ["created", "invited", "registration_details", "onboarding_profile", "onboarding_assets", "onboarding_risk", "onboarding_complete"]

// Columns the backend can order on (see SORTABLE_COLUMNS in FspAdmin::ClientListQuery).
const SORT_COLUMNS: Record<string, string> = {
  name: "last_name",
  email: "email",
  status: "status",
  onboarding: "completed_onboarding_at",
}

export const ClientsTab: React.FC<Props> = ({ organizationId: _organizationId }) => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [filters, setFilters] = useState<ClientListFilters>({})
  const [sort, setSort] = useState<SortState>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Set when the selection resolves to a single wealth manager; doubles as the
  // modal's open flag, since there is nothing to transfer from without it.
  const [transferSource, setTransferSource] = useState<{ id: number; name: string } | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportMessage, setExportMessage] = useState<string | null>(null)
  const [selectionError, setSelectionError] = useState<string | null>(null)
  // The filter itself only needs the id, but the picker lives inside a column
  // menu that unmounts every time it closes — so the name has to be held out
  // here or reopening the menu shows an empty search box instead of the wealth
  // manager currently being filtered on.
  const [wealthManagerFilterName, setWealthManagerFilterName] = useState<string | null>(null)

  const sortParam = sort ? `${sort.key} ${sort.dir}` : undefined

  // Bumped whenever the selection is invalidated, so an in-flight select-all
  // can tell whether the ids it fetched still describe what's on screen.
  const selectionGeneration = useRef(0)

  // Only a filter change alters which clients match, so only a filter change
  // invalidates the selection. Paging and sorting reorder the same set, and the
  // selection deliberately survives them — select-all covers every page.
  useEffect(() => {
    selectionGeneration.current += 1
    setSelectedIds(new Set())
    setSelectionError(null)
  }, [filters])

  const clientsQuery = useQuery(
    ["fsp-admin-clients", page, perPage, filters, sortParam],
    () => api.clients.index({ page, per_page: perPage, ...filters, sort: sortParam }),
    { keepPreviousData: true }
  )

  const exportMutation = useMutation(
    () => api.clients.export({ ids: Array.from(selectedIds).map(Number) }),
    {
      onSuccess: (res: any) => {
        setExportOpen(false)
        setExportMessage(res?.message || "Export queued")
        // The queued job owns the ids now; leaving the ticks on invites the
        // admin to re-export the same set. Failures keep the selection so a
        // retry doesn't mean re-picking everything.
        setSelectedIds(new Set())
      },
      onError: (err: any) => {
        setExportOpen(false)
        setExportMessage(err?.message || "Export failed")
      },
    }
  )

  const bulkTransferMutation = useMutation(
    (payload: BulkTransferPayload) => api.clientTransfers.bulkCreate(payload),
    {
      onSuccess: () => {
        setSelectedIds(new Set())
        queryClient.invalidateQueries(["fsp-admin-clients"])
      },
    }
  )

  const statusOptions = useMemo(() => STATUS_OPTIONS.map((s) => ({ value: s, label: humanizeString(s) })), [])

  const rows: ClientRow[] = clientsQuery.data?.data ?? []
  const meta = clientsQuery.data?.meta ?? { page: 1, pages: 1, count: 0, items: perPage }

  const toggleSelected = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  // Header checkbox selects every client matching the current filters, not just
  // the page on screen — the ids come from the server, since the other pages
  // aren't loaded.
  const allMatchingSelected = meta.count > 0 && selectedIds.size >= meta.count

  // The generation is passed as the mutation variable so both callbacks can
  // drop a response that a filter change has already made stale — otherwise it
  // lands after the effect above cleared the selection and re-ticks clients
  // that no longer match.
  const selectAllMutation = useMutation((_generation: number) => api.clients.ids(filters), {
    onSuccess: (res, generation) => {
      if (generation !== selectionGeneration.current) return
      setSelectedIds(new Set(res.ids.map(String)))
    },
    onError: (_err, generation) => {
      if (generation !== selectionGeneration.current) return
      setSelectionError("Couldn't select every client. Please try again.")
    },
  })

  const toggleSelectAll = () => {
    setSelectionError(null)
    if (allMatchingSelected) {
      setSelectedIds(new Set())
      return
    }
    selectAllMutation.mutate(selectionGeneration.current)
  }

  const handleFilterChange = (patch: Partial<ClientListFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const columns: TableColumn<ClientRow>[] = [
    { key: "name", label: "Name", sortable: true, sortKey: SORT_COLUMNS.name, render: (r) => r.attributes.fullName },
    {
      key: "email",
      label: "Email",
      sortable: true,
      sortKey: SORT_COLUMNS.email,
      render: (r) => <span className="wb-clients__email">{r.attributes.email}</span>,
    },
    {
      key: "wm",
      label: "Wealth Manager",
      filterable: true,
      filterContent: (
        <SearchableSelect
          queryKey="fsp-admin-client-wm-filter"
          fetcher={(q) => api.wealthManagers.index(q)}
          value={filters.wealth_manager_id ?? null}
          selectedName={wealthManagerFilterName}
          onChange={(id, name) => {
            setWealthManagerFilterName(name)
            handleFilterChange({ wealth_manager_id: id ?? undefined })
          }}
          placeholder="All wealth managers"
        />
      ),
      render: (r) => r.attributes.wealthManagerName ?? "—",
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      sortKey: SORT_COLUMNS.status,
      filterable: true,
      filterOptions: statusOptions,
      render: (r) => humanizeString(r.attributes.status),
    },
    {
      key: "onboarding",
      label: "Onboarding",
      sortable: true,
      sortKey: SORT_COLUMNS.onboarding,
      render: (r) => (r.attributes.completedOnboardingAt ? "Yes" : "No"),
    },
  ]

  const openTransfer = () => {
    if (selectedIds.size === 0) {
      setSelectionError("Select at least one client to transfer.")
      return
    }
    const selected = rows.filter((r) => selectedIds.has(r.id))

    // A wealth-manager filter pins the source no matter how many pages the
    // selection spans.
    if (filters.wealth_manager_id) {
      setSelectionError(null)
      setTransferSource({
        id: filters.wealth_manager_id,
        name:
          selected[0]?.attributes.wealthManagerName ?? `Wealth manager #${filters.wealth_manager_id}`,
      })
      return
    }

    // Without that filter the source has to be read off the rows, and only the
    // current page is loaded. BulkTransferClients rejects any client that isn't
    // the source's, so a wrong guess would fail server-side after the admin had
    // already confirmed — ask them to narrow it down instead.
    if (selected.length !== selectedIds.size) {
      setSelectionError(
        "Filter by wealth manager before transferring a selection that spans more than this page."
      )
      return
    }

    const wmIds = new Set(selected.map((r) => r.attributes.wealthManagerId))
    if (wmIds.size > 1) {
      setSelectionError("All selected clients must belong to the same wealth manager to transfer.")
      return
    }
    // A uniform selection can still be uniformly unassigned, and the transfer
    // endpoint requires a source wealth manager.
    const [wealthManagerId] = Array.from(wmIds)
    if (wealthManagerId == null) {
      setSelectionError("The selected clients have no wealth manager to transfer from.")
      return
    }
    setSelectionError(null)
    setTransferSource({
      id: wealthManagerId,
      name: selected[0].attributes.wealthManagerName ?? `Wealth manager #${wealthManagerId}`,
    })
  }

  const openExport = () => {
    if (selectedIds.size === 0) {
      setSelectionError("Select at least one client to export.")
      return
    }
    setSelectionError(null)
    setExportMessage(null)
    setExportOpen(true)
  }

  const selectionCount = selectedIds.size

  return (
    <div>
      <div className="wb-clients__header">
        <div className="wb-clients__heading">
          <h1>Clients</h1>
          <div className="wb-clients__subtitle">
            {meta.count} {meta.count === 1 ? "client" : "clients"}
            {selectAllMutation.isLoading && " · selecting…"}
            {/* Say "all" when the selection covers every page, so it's clear the
                ticks aren't limited to what's on screen. */}
            {!selectAllMutation.isLoading && selectionCount > 0 &&
              ` · ${allMatchingSelected ? "all " : ""}${selectionCount} selected`}
          </div>
        </div>
        <div className="wb-clients__toolbar">
          <SearchBox
            onSearch={(value) => handleFilterChange({ q: value || undefined })}
            placeholder="Search client name / email / ID number"
          />
          <button
            type="button"
            className="wb-clients__add"
            onClick={openExport}
            disabled={selectionCount === 0 || exportMutation.isLoading}
            title={selectionCount === 0 ? "Select at least one client to export" : undefined}
          >
            Export CSV{selectionCount > 0 ? ` (${selectionCount})` : ""}
          </button>
          <button
            type="button"
            className="wb-clients__add"
            onClick={openTransfer}
            disabled={selectionCount === 0}
            title={selectionCount === 0 ? "Select at least one client to transfer" : undefined}
          >
            Bulk Transfer{selectionCount > 0 ? ` (${selectionCount})` : ""}
          </button>
        </div>
      </div>

      {exportMessage && (
        <div className="fsp-admin__notice">{exportMessage}</div>
      )}

      {selectionError && (
        <div className="fsp-admin__notice fsp-admin__notice--error">{selectionError}</div>
      )}

      {clientsQuery.isError ? (
        <div className="fsp-admin__notice fsp-admin__notice--error">
          <p style={{ margin: "0 0 8px" }}>
            Failed to load clients{(clientsQuery.error as any)?.message ? `: ${(clientsQuery.error as any).message}` : ""}.
          </p>
          <Button secondary onClick={() => clientsQuery.refetch()}>Retry</Button>
        </div>
      ) : (
        <Table
          data={rows}
          columns={columns}
          sort={sort}
          onSortChange={(next) => {
            setPage(1)
            setSort(next)
          }}
          activeFilters={{
            status: filters.status ?? null,
            wm: filters.wealth_manager_id != null ? String(filters.wealth_manager_id) : null,
          }}
          onFilterChange={(key, value) => {
            if (key === "status") handleFilterChange({ status: value ?? undefined })
          }}
          page={meta.page}
          pageCount={meta.pages}
          onPageChange={setPage}
          selection={{
            selectedIds,
            onToggle: toggleSelected,
            onToggleAll: toggleSelectAll,
          }}
          loading={clientsQuery.isLoading}
          emptyMessage="No clients found."
        />
      )}

      <ConfirmDialog
        open={exportOpen}
        title="Export clients to CSV"
        message={
          <p style={{ margin: 0 }}>
            Export <strong>{selectionCount}</strong> selected {selectionCount === 1 ? "client" : "clients"} to CSV?
            We’ll email it to you when it’s ready.
          </p>
        }
        confirmLabel="Export"
        busy={exportMutation.isLoading}
        onConfirm={() => exportMutation.mutate()}
        onClose={() => setExportOpen(false)}
      />

      <BulkTransferModal
        selectedClientIds={Array.from(selectedIds).map(Number)}
        fromWealthManager={transferSource}
        onClose={() => setTransferSource(null)}
        onSubmit={async (toId) => {
          // Unreachable — the modal only renders with a source. If it fires,
          // the selection lost its shared wealth manager mid-submit.
          if (!transferSource) {
            Sentry.captureMessage("Bulk transfer submitted without a source wealth manager")
            throw new Error("Lost the source wealth manager. Close the dialog and try again.")
          }
          await bulkTransferMutation.mutateAsync({
            from_wealth_manager_id: transferSource.id,
            to_wealth_manager_id: toId,
            client_ids: Array.from(selectedIds).map(Number),
          })
        }}
        submitting={bulkTransferMutation.isLoading}
      />
    </div>
  )
}
